// cSpell:ignore Aandeel aandeel afrekening Bedrag betaald betaalmethode Betaalmethode betaalstatus Betaaluitnodiging betaling Bewoond Eigenaar Eigenaarsaandeel Factuurinformatie huurder Kost kost laste leegstand Leegstand naar Opgenomen ownerbase Prorata rekenen Scenariolaan Toewijzing Totaal Verdeling Verdelingsmethode verzonden vragen
// A cost's detail (Lewis, 2026-10-19): everything the cost popup
// (InvoiceModal) asked when the cost was entered, in the popup's own words
// and order, in at most three cards. "Kost": the popup's Factuurinformatie
// (the period on one line) and, under a thin line, its Ten laste &
// betaalmethode. "Verdeling van de kost": a building's cost split at entry
// first its Verdelingsmethode, then the split as staging shows it, the
// cost's period under the title. "Detail betaalstatus huurder": only for a
// cost with payment requests of its own or marked paid; where a cost going
// through a settlement stands, the header's status says. A field the popup
// hides for this kind of cost is left out; a field it asks that holds
// nothing reads "—". Above the cards "{category} - {supplier}" with the
// cost's type and status. No Toewijzing, Status or Bedrag card: the split's
// totals end on the cost's total excl. VAT with its VAT.
import Spacer, {
  ESpacerWeight,
} from "@rentiohq/shared-frontend/dist/components/components/Spacer";
import * as documentHooks from "@rentiohq/shared-frontend/dist/reduxV2/documents/document.hooks";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import {
  Banner,
  DataTable,
  DropdownMenu,
  Icon,
  Lozenge,
  Page,
  Stack,
  TextStyle,
} from "@rentiohq/web-shared/dist/components";
import { FontWeights } from "@rentiohq/web-theme";
import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { withBilledTarget } from "../chargeTarget.utils";
import { costLevelOf } from "../costLevels.utils";
import { costChargedToTexts, costStatusTexts } from "../costListColumns.utils";
import { isGroupCostDetail, showsOwnPartOnly } from "../costOwnPart.utils";
import { costDayText } from "../costPeriod.utils";
import {
  costDetailSettledInfo,
  costSettlementStateOf,
  costSettlementStates,
  isPartlySettledAtDeparture,
} from "../costSettlementState.utils";
import { costListStatus } from "../costsList.utils";
import {
  costBillingOf,
  costRowsVat,
  outgoingVatPctByLease,
} from "../costVat.utils";
import { buildingBreakdown, prorateByOccupancy } from "../engine";
import { unitAddressLabel, unitsExistingSince } from "../groupUnits.utils";
import {
  useAssignableProperties,
  useGroupRentalContracts,
  useGroupUnits,
  useKeys,
  usePropertyRentalContractsState,
  useSettlements,
} from "../hooks";
import { keyDisplayName } from "../keyName.utils";
import { toBrusselsIsoDay } from "../localDay.utils";
import {
  fromCents,
  tenantShareCents,
  toCents,
  vatOfAmount,
} from "../money.utils";
import { formatWeight } from "../numberInput.utils";
import { IOccupiedDays } from "../occupiedDays.utils";
import {
  confirmedCostShareContext,
  costSplitFromContext,
  ICostShareContext,
  ICostSplit,
  ICostSplitParty,
  ICostSplitRow,
  unitCostShareText,
} from "../scenes/SettlementInfo/SettlementForm/steps/costShareCalculation.utils";
import {
  CollapsibleCard,
  DetailTableWrap,
} from "../scenes/SettlementInfo/SettlementForm/steps/shared";
import { confirmedCardDistribution } from "../scenes/SettlementInfo/SettlementForm/steps/summaryCards.utils";
import { settlementDisplayName } from "../settlementName.utils";
import {
  frozenContractDates,
  normalizeGroupSettlementCosts,
  withFrozenFigures,
} from "../settlementView.utils";
import {
  CATEGORY_LABELS_NL,
  ContractRef,
  ICostDistribution,
  Invoice,
  isOwnerOnlyCategory,
  PaymentSplit,
  settlementEligible,
  vatPercent,
} from "../types";
import { useWithArchivedProperties } from "../useArchivedProperties";
import { ListMenuButton } from "./listTable.styles";
import { ListTypeLozenge } from "./ListTypeLozenge";
import { Money, MoneyWithVat } from "./Money";
import { OccupiedDays } from "./OccupiedDays";

// "16/10/2026" of a calendar day or a moment the API sends.
const dayText = (value?: string): string =>
  costDayText(toBrusselsIsoDay(value));

const CONSUMPTION_UNIT_LABELS: Record<
  NonNullable<ICostDistribution["consumptionUnit"]>,
  string
> = { kwh: "kWh", m3: "m³" };

// The popup's names of its three distribution methods.
const METHOD_KEYS: Record<ICostDistribution["method"], string> = {
  key: "costs.distribution_method.key",
  consumption: "costs.distribution_method.consumption",
  amount: "costs.distribution_method.company",
};

// Whether a payment was asked from a tenant through Rentio: the splits'
// links say it, the cost-level date only for a cost from before splits (it
// outlives a payment that was withdrawn since). The popup's own rule
// (InvoiceModal hasSentPayment).
const isPaymentRequested = (invoice: Invoice): boolean => {
  const splits = invoice.paymentSplits ?? [];
  return (
    (!!invoice.paymentRequestedAt && splits.length === 0) ||
    splits.some(
      split =>
        !!split.paymentOrderId ||
        !!split.requestedAt ||
        split.paymentRequestId != null,
    )
  );
};

// The popup's "Betaalmethode" of a cost a tenant bears: marked as paid by
// the tenant, asked right away, or left to the settlement.
const paymentMethodKey = (invoice: Invoice): string => {
  if (invoice.paidAt) return "costs.modal.method.paid";
  if (costBillingOf(invoice) === "immediate") {
    return "costs.modal.method.direct";
  }
  return "costs.modal.method.settlement";
};

// ── Layout ──────────────────────────────────────────────────────────────────

// A part under which the amount is too small to show (a fraction of a cent).
const HALF_CENT = 0.005;

const LABEL_WIDTH = "160px";

// The space between two rows of a card.
const ROW_GAP_PX = 12;

// The cards one under the other, spaced as the dashboard spaces its cards
// (a card after a card keeps its own margin).
const Cards = styled.div`
  min-width: 0;
`;

const HeaderLozenges = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
`;

// The rows of a card: the popup's label, muted, and what was entered.
const Fields = styled.dl`
  display: grid;
  grid-template-columns: ${LABEL_WIDTH} minmax(0, 1fr);
  row-gap: ${ROW_GAP_PX}px;
  column-gap: 16px;
  margin: 0;
  dt {
    font-size: 14px;
    line-height: 20px;
    color: ${({ theme }) => theme.getColor("gray", 600)};
  }
  dd {
    margin: 0;
    font-size: 14px;
    line-height: 20px;
    color: ${({ theme }) => theme.getColor("gray", 1000)};
    overflow-wrap: anywhere;
  }
`;

// The thin line between a card's two groups of rows, twice the rows' own
// space above and under it.
const FieldsDivider = styled.hr`
  margin: ${ROW_GAP_PX * 2}px 0;
  border: 0;
  border-top: 1px solid ${({ theme }) => theme.getColor("gray", 200)};
`;

const FieldNote = styled.div`
  margin-top: 4px;
  font-size: 13px;
  line-height: 18px;
  color: ${({ theme }) => theme.getColor("gray", 600)};
`;

const Inline = styled.span`
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 8px;
`;

const Dash: React.FC = () => <TextStyle variation="subdued">—</TextStyle>;

// One row of a card; without a value it reads "—".
const Field: React.FC<{
  label: string;
  note?: React.ReactNode;
  children?: React.ReactNode;
}> = ({ label, note, children }) => (
  <>
    <dt>{label}</dt>
    <dd>
      {children ?? <Dash />}
      {note && <FieldNote>{note}</FieldNote>}
    </dd>
  </>
);

// ── Verdeling van de kost ───────────────────────────────────────────────────
// A card like the drawer's other cards, its title in their size, font and
// weight (Lewis, 2026-10-19), the cost's period under it; in it staging's
// table Wie · Aandeel · Bedrag with "+ € x btw" under each amount and a
// heavy line above "Totaal van de kost" (DetailTableWrap); a building's cost
// split at entry one card per unit, the cost's total under them.

// A table of only totals shows no headings.
const SplitTableWrap = styled(DetailTableWrap)`
  &.is-totals-only thead {
    display: none;
  }
`;

// The cost's total under a building's per-unit cards, its amount on their
// amount column's edge (a card's section padding, 20px).
const CardInset = styled.div`
  padding: 0 20px;
`;

// One row of a split: who, their share, the days they lived there and the
// amount excl. VAT with its VAT. The party whose summary row opened the
// cost is tinted.
interface ISplitTableRow {
  id: string;
  who: React.ReactNode;
  share: React.ReactNode;
  // "31/92": a column of its own, "Prorata", in a table whose split goes by
  // the days (Lewis, 2026-10-19), the full sentence in its hover.
  occupied?: IOccupiedDays;
  amount: number;
  vat: number;
  isOpened?: boolean;
}

// "Wie": the party on the first line, small and muted under it the owner
// and the unit's full address (Lewis, 2026-10-19).
const WhoCell: React.FC<{ title: string; notes: string[] }> = ({
  title,
  notes,
}) => (
  <div>
    <div>{title}</div>
    {notes.map(note => (
      <TextStyle key={note} variation="subdued" size="small" element="div">
        {note}
      </TextStyle>
    ))}
  </div>
);

// Staging's table of a split, its totals row last (no total incl. VAT:
// Lewis, 2026-10-19). Without rows (a cost not split yet) only its totals,
// no headings. The total's VAT stays on one line ("+ € 131,35 btw"
// wrapped its "btw" in a narrow drawer). A split by the days shows
// "Aandeel" and "Prorata" side by side, both centered: "Prorata", not
// "Bewoond", since the table holds a tenant's days and the days a unit
// stood empty, each row saying which (Lewis, 2026-10-19). Every heading on
// one line, no "?": the "i" of a summary row's amount says the same with
// its labels.
const SplitTable: React.FC<{
  rows: ISplitTableRow[];
  totalLabel: string;
  total: { amount: number; vat: number };
  withOccupied?: boolean;
}> = ({ rows, totalLabel, total, withOccupied }) => (
  <SplitTableWrap className={rows.length === 0 ? "is-totals-only" : undefined}>
    <DataTable
      columnContentTypes={
        withOccupied
          ? ["text", "text-centered", "text-centered", "numeric"]
          : ["text", "numeric", "numeric"]
      }
      headings={[
        getLocalizedText("costs.detail.who"),
        getLocalizedText("costs.detail.share"),
        ...(withOccupied ? [getLocalizedText("costs.detail.split.days")] : []),
        getLocalizedText("costs.field.amount"),
      ]}
      showTotalsInFooter
      totals={[
        totalLabel,
        "",
        ...(withOccupied ? [""] : []),
        <div style={{ whiteSpace: "nowrap" }}>
          <MoneyWithVat amount={total.amount} vat={total.vat} />
        </div>,
      ]}
      rows={rows.map(row => ({
        id: row.id,
        isSelected: row.isOpened,
        content: [
          row.who,
          <div style={{ whiteSpace: "nowrap" }}>{row.share}</div>,
          ...(withOccupied
            ? [
                <div style={{ whiteSpace: "nowrap" }}>
                  {row.occupied ? (
                    <OccupiedDays occupied={row.occupied} />
                  ) : (
                    "—"
                  )}
                </div>,
              ]
            : []),
          <MoneyWithVat amount={row.amount} vat={row.vat} />,
        ],
      }))}
    />
  </SplitTableWrap>
);

// Only the cost's total: a cost not split yet.
const CostTotals: React.FC<{ amount: number; vat: number }> = ({
  amount,
  vat,
}) => (
  <SplitTable
    rows={[]}
    totalLabel={getLocalizedText("costs.detail.cost_total")}
    total={{ amount, vat }}
  />
);

const sameUnitSet = (a: string[], b: string[]): boolean =>
  a.length === b.length && a.every(id => b.includes(id));

// The row of the party whose summary row opened the cost: a tenant's lease,
// or an owner's units.
const isOpenedSplitRow = (
  row: ICostSplitRow,
  openedFor?: ICostSplitParty,
): boolean => {
  if (openedFor?.contractId) return row.contractId === openedFor.contractId;
  if (!openedFor?.ownerUnitIds || row.kind === "tenant" || !row.ownerUnitIds) {
    return false;
  }
  return sameUnitSet(
    row.ownerUnitIds.map(String),
    openedFor.ownerUnitIds.map(String),
  );
};

// A settlement's split of the cost as the table's rows: "Anna" with her
// unit's address under it, the unit's share, the days the tenant lived
// there, the amount excl. VAT; an owner's parts one row per unit, the
// owner and the unit's address under "Leegstand" or "Eigenaar (aandeel
// kost 50%)" (Lewis, 2026-10-19).
const settlementSplitRows = (
  split: ICostSplit,
  openedFor?: ICostSplitParty,
): ISplitTableRow[] =>
  split.rows.map(row => ({
    id: row.id,
    who: <WhoCell title={row.title} notes={row.notes} />,
    share: row.share ?? "—",
    occupied: row.occupied,
    amount: fromCents(toCents(row.amount) - toCents(row.vat)),
    vat: row.vat,
    isOpened: isOpenedSplitRow(row, openedFor),
  }));

// ── A tenant's payment ──────────────────────────────────────────────────────

// A status lozenge whole, never cut short with "…" (Lewis, 2026-10-19:
// "Betaaluitnodiging verzonden"): the row around it wraps first, and only a
// line narrower than the lozenge itself wraps its text inside it.
const WholeLozenge = styled.span`
  display: inline-flex;
  max-width: 100%;
  > span {
    max-width: 100%;
    max-height: none;
    overflow: visible;
    text-overflow: clip;
    white-space: normal;
  }
`;

const StatusLozenge: React.FC<{
  appearance: React.ComponentProps<typeof Lozenge>["appearance"];
  children: React.ReactNode;
}> = ({ appearance, children }) => (
  <WholeLozenge>
    <Lozenge appearance={appearance}>{children}</Lozenge>
  </WholeLozenge>
);

// Where the payment a split asked stands: paid (the split's own day, else
// the day the cost was marked paid), asked, or still to ask.
const SplitPaymentLozenge: React.FC<{
  split: PaymentSplit;
  paidAt?: string;
}> = ({ split, paidAt }) => {
  const paidDay = split.paidAt ?? paidAt;
  if (paidDay) {
    return (
      <StatusLozenge appearance="success">
        {getLocalizedText("costs.detail.paid_on", { date: dayText(paidDay) })}
      </StatusLozenge>
    );
  }
  if (split.requestedAt || split.paymentOrderId) {
    return (
      <StatusLozenge appearance="info">
        {getLocalizedText("costs.status.invitation_sent")}
      </StatusLozenge>
    );
  }
  return (
    <StatusLozenge appearance="warning">
      {getLocalizedText("costs.status.to_request")}
    </StatusLozenge>
  );
};

// One payment request in staging's layout: who pays, with their unit and
// their amount and its VAT under the name, the status, then "Ga naar
// betaling" behind the menu. The row wraps before the lozenge is cut.
const PaymentRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;
  padding: 10px 0;
  & + & {
    border-top: 1px solid ${({ theme }) => theme.getColor("gray", 200)};
  }
  .who {
    flex: 1 1 180px;
    min-width: 0;
  }
  .name {
    font-weight: ${FontWeights.Medium};
    overflow-wrap: anywhere;
  }
  .sub {
    display: flex;
    flex-wrap: wrap;
    gap: 0 6px;
    font-size: 12px;
    color: ${({ theme }) => theme.getColor("gray", 600)};
  }
  .actions {
    display: flex;
    align-items: center;
    margin-left: auto;
  }
`;

export interface InvoiceViewModalProps {
  invoice: Invoice;
  propertyLabel?: string;
  propertyType?: "unit" | "building";
  onEdit?: () => void;
  onDelete?: () => void;
  // Set when this cost is already part of a confirmed afrekening, as the
  // drawer that shows it knows from the settlements it fetched. The
  // settlements the detail finds holding the cost come first (see
  // costDetailSettledInfo).
  settledInfo?: { settled: boolean; date?: string };
  // Opened from the wizard's summary: the draft's figures, so a last card
  // shows how the settlement splits this cost, with the name it goes by.
  // From the cost list there is no such card.
  splitContext?: ICostShareContext;
  settlementName?: string;
  // The summary row that opened the cost: its party is tinted in the split.
  openedFor?: ICostSplitParty;
}

export const InvoiceViewModal: React.FC<InvoiceViewModalProps> = ({
  invoice: storedInvoice,
  propertyLabel,
  propertyType,
  onEdit,
  onDelete,
  settledInfo,
  splitContext,
  settlementName,
  openedFor,
}) => {
  // Shown the way the settlement bills it: an older ownership cost that still
  // names the tenant is the owner's (withBilledTarget).
  const invoice = useMemo(
    () => withBilledTarget(storedInvoice),
    [storedInvoice],
  );
  const splits = useMemo(
    () => invoice.paymentSplits ?? [],
    [invoice.paymentSplits],
  );
  const vatRate = vatPercent(invoice.vatPct);
  const btw = vatOfAmount({
    amountExclVat: invoice.amountExclVat,
    vatPct: vatRate,
  });
  const categoryText = CATEGORY_LABELS_NL[invoice.category];
  const isOwnerCost = invoice.target === "owner";
  const billing = costBillingOf(invoice);
  const outgoingVat = useMemo(() => outgoingVatPctByLease(splits), [splits]);

  // The uploaded invoice document (id stored in `documentUrl`) — fetch it so
  // the user can open it straight from the cost.
  const { detail: invoiceDocument } = documentHooks.useDetail({
    id: invoice.documentUrl,
    shouldRefetch: !!invoice.documentUrl,
  });
  // Only treat an https document URL as a safe href — guards against a
  // malformed or `javascript:` URL from the API being executed as script.
  const invoiceDocumentHref =
    invoiceDocument?.url && invoiceDocument.url.startsWith("https://")
      ? invoiceDocument.url
      : undefined;

  const { items: activeProperties, isFetching: isFetchingProperties } =
    useAssignableProperties();
  // The property list leaves archived properties out: the cost's own
  // property, when archived, is added so it keeps its address and its group.
  const assignable = useWithArchivedProperties({
    properties: activeProperties,
    isFetching: isFetchingProperties,
    ids: [invoice.unitId],
  });
  const ownProperty = assignable.find(p => p.id === invoice.unitId);
  // Its address with the box spelled out, "Scenariolaan 1 bus 1, 9000 Gent",
  // as the summary and the cost choice write it (unitAddressLabel).
  const ownAddress = ownProperty ? unitAddressLabel(ownProperty) : undefined;
  // A cost on a building: told by the screen that opened it, else by the
  // property it is on; also for the owner of one unit, who cannot read the
  // group the cost is on (isGroupCostDetail).
  const isBuilding =
    invoice.scope === "common" ||
    isGroupCostDetail({
      invoice,
      propertyType: propertyType ?? ownProperty?.type,
    });
  const propertyIdNum =
    invoice.unitId && /^\d+$/.test(invoice.unitId)
      ? +invoice.unitId
      : undefined;
  const { rentals: singleRentals, isLoaded: areSingleRentalsLoaded } =
    usePropertyRentalContractsState(!isBuilding ? propertyIdNum : undefined);
  // A unit's building, for its type and its settlements; a group-cost child
  // (a unit cost from before the split moved to the settlement) is the
  // building's.
  const unitParentGroupId = isBuilding ? undefined : ownProperty?.parentId;
  const parentGroupIdForChild = invoice.groupCostRef
    ? unitParentGroupId
    : undefined;
  const buildingGroupId = isBuilding ? invoice.unitId : parentGroupIdForChild;
  const { units: groupUnits, isFetching: isFetchingGroupUnits } =
    useGroupUnits(buildingGroupId);

  // Is this cost locked into a confirmed settlement? A cost can be consumed
  // by a settlement on its own property, or by a settlement of its parent
  // building. A building cost can also sit in the departure settlements of
  // its units.
  const departureUnitIds = useMemo(
    () => (isBuilding ? groupUnits.map(unit => String(unit.id)) : []),
    [isBuilding, groupUnits],
  );
  const ownSettlementsFilter = useMemo(
    () => ({ unitId: invoice.unitId, unitIds: departureUnitIds }),
    [invoice.unitId, departureUnitIds],
  );
  const parentSettlementsFilter = useMemo(
    // Without a parent group this resolves to the same query as above, which
    // the redux cache dedupes — hooks must run unconditionally.
    () =>
      unitParentGroupId
        ? { groupId: unitParentGroupId }
        : { groupId: invoice.unitId, unitIds: departureUnitIds },
    [unitParentGroupId, invoice.unitId, departureUnitIds],
  );
  const { items: ownSettlements } = useSettlements(ownSettlementsFilter);
  const { items: parentSettlements } = useSettlements(parentSettlementsFilter);
  // The confirmed settlement the cost went into: it holds the cost's split
  // from then on (frozen in its snapshot). Departure settlements that
  // settled a tenant's share leave the rest open and the cost editable.
  const settlementState = useMemo(
    () =>
      costSettlementStateOf(
        costSettlementStates([...ownSettlements, ...parentSettlements]),
        invoice.id,
      ),
    [ownSettlements, parentSettlements, invoice.id],
  );
  const { settledIn } = settlementState;
  const isSettled = !!settledIn;
  // One state for the header and the split, so the detail never reads
  // settled and partly settled at once.
  const shownSettledInfo = costDetailSettledInfo({
    state: settlementState,
    openedFrom: settledInfo,
  });
  const settledName = settledIn
    ? settlementDisplayName(settledIn)
    : splitContext?.lockedIn;

  // The units that were part of the group during the cost's period, the same
  // set the cost popup splits over (see groupUnits.utils).
  const buildingUnits = useMemo(
    () => unitsExistingSince({ units: groupUnits, from: invoice.periodFrom }),
    [groupUnits, invoice.periodFrom],
  );
  const buildingUnitIds = useMemo(
    () => buildingUnits.map(u => u.id),
    [buildingUnits],
  );
  const buildingContracts = useGroupRentalContracts(buildingUnitIds);
  // A building's cost a confirmed settlement holds: split as that settlement
  // split it, from its frozen snapshot (the figures, the keys' weights and
  // names and the rental periods it froze), as its Detail tab shows it.
  // Opened from a settlement, that settlement's own figures say it.
  const { items: groupKeys } = useKeys(
    isBuilding && settledIn?.groupId ? settledIn.groupId : "",
  );
  const settledSplit = useMemo(() => {
    if (!isBuilding || !settledIn || splitContext) return undefined;
    const { snapshot } = settledIn;
    const [settledInvoice] = normalizeGroupSettlementCosts(
      [invoice],
      settledIn,
    ).map(cost => withFrozenFigures(cost, snapshot));
    const contracts = buildingContracts.map(rental => rental.ref);
    return costSplitFromContext({
      invoice: settledInvoice,
      context: confirmedCostShareContext({
        settlement: settledIn,
        catMeta: {},
        distribution: confirmedCardDistribution({
          settlement: settledIn,
          costs: [settledInvoice],
          keys: groupKeys,
          assignable,
        }),
        leaseDatesOf: pc =>
          frozenContractDates({
            snapshot,
            contractId: pc.contractId,
            contracts,
          }),
        assignable,
      }),
    });
  }, [
    isBuilding,
    settledIn,
    splitContext,
    invoice,
    buildingContracts,
    groupKeys,
    assignable,
  ]);
  // The split a group cost billed right away was billed with (its key,
  // consumption or amounts per unit), as the cost popup stored it.
  const storedWeights = invoice.distribution?.weights;
  // The units the stored split ran over, in the group's order (the order the
  // popup split them in): a unit that left the group since keeps its part.
  const splitUnits = useMemo(() => {
    if (!storedWeights) return buildingUnits;
    const leftUnits = Object.keys(storedWeights)
      .filter(id => !buildingUnits.some(u => String(u.id) === id))
      .map(id => ({
        id: Number(id),
        label:
          groupUnits.find(u => String(u.id) === id)?.label ??
          assignable.find(p => p.id === id)?.address ??
          id,
      }));
    return [...buildingUnits, ...leftUnits];
  }, [buildingUnits, groupUnits, storedWeights, assignable]);
  // A unit by its full address with its box spelled out (unitAddressLabel),
  // as the summary names it (Lewis, 2026-10-19).
  const unitLabelOf = (unitId: string): string => {
    const property = assignable.find(p => String(p.id) === unitId);
    if (property) return unitAddressLabel(property);
    return splitUnits.find(u => String(u.id) === unitId)?.label ?? unitId;
  };

  // A building cost is split over its units at entry only when it is settled
  // right away (a payment asked, or marked paid): the popup then asks for
  // the distribution method and shows the split. A cost left to the
  // settlement is one amount on the building, split in that settlement.
  // `paymentSplits` is where such a split is stored.
  const isSplitAtEntry =
    isBuilding && !isOwnerCost && (splits.length > 0 || !!invoice.distribution);
  // An owner who sees only their part of a group with other owners reads
  // only what their own tenants were billed (showsOwnPartOnly).
  const isOwnPartOnly = showsOwnPartOnly(invoice);

  // "Ga naar betaling" → the payment-order page of a split's payment.
  const navigate = useNavigate();
  const goToPayment = (orderId: string) => {
    const propertyId = invoice.unitId ?? invoice.groupId;
    if (!propertyId) return;
    navigate(`/properties/${propertyId}/payments?paymentOrderId=${orderId}`);
  };

  // Opened from a settlement: how it split this cost, in its own figures
  // (the wizard's draft, or a confirmed settlement's snapshot).
  const settlementSplit = useMemo(
    () =>
      splitContext
        ? costSplitFromContext({ invoice, context: splitContext })
        : undefined,
    [splitContext, invoice],
  );
  const splitSettlementName = settlementName ?? splitContext?.lockedIn;

  // ── Header ────────────────────────────────────────────────────────────────

  const titleText = [invoice.categoryOther || categoryText, invoice.supplier]
    .map(part => part?.trim())
    .filter(Boolean)
    .join(" - ");

  // Its type, as the cost list labels it inside a building.
  const typeBuildingId = isBuilding ? invoice.unitId : unitParentGroupId;
  const renderTypeLozenge = () => {
    if (!typeBuildingId) return null;
    const isCommon =
      costLevelOf({ invoice, buildingId: typeBuildingId }) === "building";
    return (
      <ListTypeLozenge
        label={getLocalizedText(
          isCommon ? "costs.label.common" : "costs.label.private",
        )}
        appearance={isCommon ? "info" : "default"}
        address={isCommon ? undefined : ownAddress ?? propertyLabel}
      />
    );
  };

  // Its status, as the cost list shows it; held by a settlement, that
  // settlement by name.
  const statusTexts = costStatusTexts({ invoice, settled: shownSettledInfo });
  const listStatus = costListStatus({
    invoice,
    settled: shownSettledInfo.settled,
    partlySettled: isPartlySettledAtDeparture(shownSettledInfo),
  });
  const renderStatusLozenge = () => {
    if (!statusTexts.appearance) return null;
    const text =
      listStatus === "in_settlement" && settledName
        ? getLocalizedText("costs.detail.status.in_settlement_named", {
            name: settledName,
          })
        : statusTexts.text;
    return (
      <Inline>
        <StatusLozenge appearance={statusTexts.appearance}>
          {text}
        </StatusLozenge>
        {statusTexts.subLines.length > 0 && (
          <TextStyle variation="subdued" size="small">
            {statusTexts.subLines.join(" · ")}
          </TextStyle>
        )}
      </Inline>
    );
  };

  const headerLozenges = (
    <HeaderLozenges>
      {renderTypeLozenge()}
      {renderStatusLozenge()}
    </HeaderLozenges>
  );

  // ── Factuurinformatie ─────────────────────────────────────────────────────

  const renderDocument = () => {
    if (!invoice.documentUrl) return undefined;
    const name =
      invoiceDocument?.filename ??
      getLocalizedText("costs.detail.open_invoice");
    if (invoiceDocumentHref) {
      return (
        <a href={invoiceDocumentHref} target="_blank" rel="noopener noreferrer">
          {name}
        </a>
      );
    }
    return invoiceDocument ? name : getLocalizedText("system.loading");
  };

  const renderInvoiceFields = () => (
    <Fields>
      <Field label={getLocalizedText("costs.detail.invoice")}>
        {renderDocument()}
      </Field>
      <Field label={getLocalizedText("costs.modal.invoice_date")}>
        {invoice.date ? costDayText(invoice.date) : undefined}
      </Field>
      <Field label={getLocalizedText("costs.field.category")}>
        {categoryText}
      </Field>
      {invoice.category === "Other" && (
        <Field label={getLocalizedText("costs.modal.specify_category")}>
          {invoice.categoryOther || undefined}
        </Field>
      )}
      <Field label={getLocalizedText("costs.field.supplier")}>
        {invoice.supplier || undefined}
      </Field>
      <Field label={getLocalizedText("costs.field.description")}>
        {invoice.description || undefined}
      </Field>
      <Field label={getLocalizedText("costs.field.period")}>
        {invoice.periodFrom && invoice.periodTo
          ? `${costDayText(invoice.periodFrom)} → ${costDayText(
              invoice.periodTo,
            )}`
          : undefined}
      </Field>
    </Fields>
  );

  // ── Ten laste & betaalmethode ─────────────────────────────────────────────

  // Only the tenant's share: the tenant's and owner's amounts are in the
  // split ("Verdeling van de kost") and the payment status (Lewis,
  // 2026-10-19).
  const renderShareFields = () => {
    if (invoice.target !== "shared") return null;
    const tenantPct = invoice.tenantSharePct;
    return (
      <Field label={getLocalizedText("costs.modal.tenant_share_pct")}>
        {tenantPct == null ? undefined : `${formatWeight(tenantPct)}%`}
      </Field>
    );
  };

  // The VAT the broker chose for the recharge to a VAT-taxable lease
  // (leaseVat.utils): a split only records a rate of our own.
  const outgoingRates = [...new Set(Object.values(outgoingVat))].sort(
    (a, b) => a - b,
  );

  const renderChargeFields = () => (
    <Fields>
      <Field
        label={getLocalizedText("costs.modal.who_bears")}
        note={
          isOwnerCost && isOwnerOnlyCategory(invoice.category)
            ? getLocalizedText("costs.modal.owner_only_hint")
            : undefined
        }
      >
        {costChargedToTexts(invoice).text}
      </Field>
      {renderShareFields()}
      {!isOwnerCost && (
        <Field label={getLocalizedText("costs.modal.payment_method")}>
          {getLocalizedText(paymentMethodKey(invoice))}
        </Field>
      )}
      {outgoingRates.length > 0 && (
        <Field label={getLocalizedText("costs.outgoing_vat.label")}>
          {outgoingRates
            .map(rate =>
              getLocalizedText("costs.outgoing_vat.rate", {
                rate: String(rate),
              }),
            )
            .join("; ")}
        </Field>
      )}
    </Fields>
  );

  // ── Kost ──────────────────────────────────────────────────────────────────
  // The popup's Factuurinformatie, then under a thin line its Ten laste &
  // betaalmethode, in one card (Lewis, 2026-10-19).

  const renderCostCard = () => (
    <CollapsibleCard title={getLocalizedText("costs.field.cost")}>
      {renderInvoiceFields()}
      <FieldsDivider />
      {renderChargeFields()}
    </CollapsibleCard>
  );

  // ── Verdelingsmethode ─────────────────────────────────────────────────────

  const renderMethodFields = (distribution: ICostDistribution) => {
    const unitIds = splitUnits
      .map(unit => String(unit.id))
      .filter(id => id in distribution.weights);
    // A unit the group no longer lists keeps its stored figure.
    Object.keys(distribution.weights).forEach(id => {
      if (!unitIds.includes(id)) unitIds.push(id);
    });
    if (distribution.method === "key") {
      return (
        <Field label={getLocalizedText("costs.distribution_method.key")}>
          {distribution.keyName
            ? keyDisplayName(distribution.keyName)
            : getLocalizedText("costs.keys.equal_split")}
        </Field>
      );
    }
    if (distribution.method === "consumption") {
      const unitText =
        CONSUMPTION_UNIT_LABELS[distribution.consumptionUnit ?? "m3"];
      return (
        <>
          <Field label={getLocalizedText("costs.modal.unit")}>{unitText}</Field>
          {unitIds.map(id => (
            <Field key={id} label={unitLabelOf(id)}>
              {`${formatWeight(distribution.weights[id] ?? 0)} ${unitText}`}
            </Field>
          ))}
        </>
      );
    }
    return unitIds.map(id => (
      <Field key={id} label={unitLabelOf(id)}>
        <Money amount={distribution.weights[id] ?? 0} />
      </Field>
    ));
  };

  const renderMethodRows = () => (
    <Fields>
      <Field label={getLocalizedText("costs.modal.step.distribution_method")}>
        {invoice.distribution
          ? getLocalizedText(METHOD_KEYS[invoice.distribution.method])
          : undefined}
      </Field>
      {invoice.distribution && renderMethodFields(invoice.distribution)}
    </Fields>
  );

  // ── Verdeling van de kost ─────────────────────────────────────────────────
  // Every cost (Lewis, 2026-10-19), in staging's layout: a unit's own cost
  // over its tenants by their days and its owner, a building's cost split at
  // entry per unit, a building's cost a settlement holds as that settlement
  // split it, and one still bound for a settlement as its total. Opened from
  // a settlement, as that settlement splits it.

  const costTotal = { amount: invoice.amountExclVat, vat: btw };
  const costTotalLabel = getLocalizedText("costs.detail.cost_total");
  // The period the occupied days of a split count in.
  const costPeriod = { from: invoice.periodFrom, to: invoice.periodTo };
  // A shared cost: the tenants' part in %, the rest the owner's share.
  const isShared = invoice.target === "shared";
  const tenantsPct = isShared ? invoice.tenantSharePct ?? 0 : 100;
  const ownerShareName = getLocalizedText("costs.detail.owner_cost_share_pct", {
    pct: String(100 - tenantsPct),
  });

  const renderLoading = () => (
    <TextStyle variation="subdued">
      {getLocalizedText("system.loading")}
    </TextStyle>
  );

  // The billed amounts of a group cost, when no split over the whole
  // building can be redone: what the viewer's own tenants were billed, or,
  // for a cost billed before the popup stored its split, every tenant and
  // the rest of the cost as the owner's.
  const renderBilledRows = (params: {
    note: string;
    withOwnerRest: boolean;
    totalKey: string;
  }) => {
    const { note, withOwnerRest, totalKey } = params;
    const tenantRows = splits.map(split => ({
      key: split.id,
      kind: "tenant" as const,
      partyId: split.id,
      name: split.payerName || getLocalizedText("costs.field.tenant"),
      amount: split.amount,
      supplierVat: split.supplierVat,
      outgoingVatPct: split.outgoingVatPct,
    }));
    const ownerPartyId = invoice.unitId ?? "";
    const ownerRows: {
      key: string;
      kind: "owner";
      partyId: string;
      name: string;
      amount: number;
    }[] = [];
    if (withOwnerRest) {
      const totalCents = toCents(invoice.amountExclVat);
      const tenantCents =
        invoice.target === "shared"
          ? tenantShareCents({
              totalCents,
              tenantSharePct: invoice.tenantSharePct ?? 0,
            })
          : totalCents;
      const billedCents = splits.reduce(
        (sum, split) => sum + toCents(split.amount),
        0,
      );
      const vacancy = fromCents(Math.max(0, tenantCents - billedCents));
      const ownerBase = fromCents(totalCents - tenantCents);
      if (vacancy > 0) {
        ownerRows.push({
          key: "vacancy",
          kind: "owner",
          partyId: ownerPartyId,
          name: getLocalizedText("costs.detail.owner_vacancy_full"),
          amount: vacancy,
        });
      }
      if (ownerBase > 0) {
        ownerRows.push({
          key: "ownerbase",
          kind: "owner",
          partyId: ownerPartyId,
          name: getLocalizedText("costs.detail.owner_cost_share_pct", {
            pct: String(100 - (invoice.tenantSharePct ?? 0)),
          }),
          amount: ownerBase,
        });
      }
    }
    const rows = [...tenantRows, ...ownerRows];
    const rowVat = costRowsVat({ rows, vatPct: vatRate, billing });
    // Only the viewer's own tenants: what they were billed, in cents.
    const total = withOwnerRest
      ? costTotal
      : {
          amount: fromCents(
            rows.reduce((sum, row) => sum + toCents(row.amount), 0),
          ),
          vat: fromCents(rowVat.reduce((sum, vat) => sum + toCents(vat), 0)),
        };
    return (
      <Stack vertical spacing="tight">
        <TextStyle variation="subdued" size="small">
          {note}
        </TextStyle>
        <SplitTable
          rows={rows.map((row, rowIndex) => ({
            id: row.key,
            who: row.name,
            share: "—",
            amount: row.amount,
            vat: rowVat[rowIndex],
          }))}
          totalLabel={getLocalizedText(totalKey)}
          total={total}
        />
      </Stack>
    );
  };

  // A building's cost split at entry: one card per unit, its tenants by
  // their days, its vacancy and its owner's share, then the cost's total.
  const renderBuildingSplit = () => {
    if (isOwnPartOnly) {
      if (splits.length === 0) {
        return (
          <TextStyle variation="subdued" size="small">
            {getLocalizedText("costs.detail.own_part_no_tenants")}
          </TextStyle>
        );
      }
      return renderBilledRows({
        note: getLocalizedText("costs.detail.own_part_only"),
        withOwnerRest: false,
        totalKey: "costs.detail.own_part_total",
      });
    }
    if (!invoice.distribution) {
      return renderBilledRows({
        note: getLocalizedText("costs.detail.distribution_not_recorded"),
        withOwnerRest: true,
        totalKey: "costs.detail.cost_total",
      });
    }
    if (isFetchingGroupUnits && splitUnits.length === 0) {
      return renderLoading();
    }
    // The unit's share of the building's cost: its weight of the key or
    // its reading ("120/900 kWh"); an amount per unit is the unit's own.
    const { distribution } = invoice;
    const consumptionUnit =
      distribution.method === "consumption"
        ? ` ${CONSUMPTION_UNIT_LABELS[distribution.consumptionUnit ?? "m3"]}`
        : "";
    const unitWeightText = (weight: number, totalWeight: number): string =>
      distribution.method !== "amount" && totalWeight > 0
        ? `${formatWeight(weight)}/${formatWeight(
            totalWeight,
          )}${consumptionUnit}`
        : "—";
    const contractsByUnit: Record<string, ContractRef[]> = {};
    buildingContracts.forEach(rental => {
      (contractsByUnit[rental.ref.unitId] ??= []).push(rental.ref);
    });
    const breakdown = buildingBreakdown({
      amount: invoice.amountExclVat,
      tenantSharePct: tenantsPct,
      units: splitUnits.map(u => ({ id: String(u.id), label: u.label })),
      contractsByUnit,
      weights: invoice.distribution.weights,
      period: { from: invoice.periodFrom, to: invoice.periodTo },
    });
    const unitTables = breakdown.units.map(unit => {
      const rows = [
        ...unit.proration.shares.map(share => ({
          kind: "tenant" as const,
          key: share.contractId,
          name: share.tenantName,
          fromIso: share.fromIso,
          days: share.days,
          amount: share.amount,
        })),
        ...unit.proration.vacancyIntervals.map((vacancy, index) => ({
          kind: "owner" as const,
          key: `vac-${unit.unitId}-${index}`,
          name: getLocalizedText("costs.detail.owner_vacancy_full"),
          fromIso: vacancy.fromIso,
          days: vacancy.days,
          amount: vacancy.amount,
        })),
      ].sort((a, b) => a.fromIso.localeCompare(b.fromIso));
      const showOwnerBase =
        isShared && Math.abs(unit.proration.ownerBase) >= HALF_CENT;
      return {
        unit,
        rows,
        showOwnerBase,
        vatRows: [
          ...rows.map(row => ({
            kind: row.kind,
            partyId: row.kind === "tenant" ? row.key : unit.unitId,
            amount: row.amount,
            outgoingVatPct:
              row.kind === "tenant" ? outgoingVat[row.key] : undefined,
          })),
          {
            kind: "owner" as const,
            partyId: unit.unitId,
            amount: showOwnerBase ? unit.proration.ownerBase : 0,
          },
        ],
      };
    });
    // Each row's part of the cost's VAT: what the settlement or the
    // tenant's payment request bills it (see costVat.utils).
    const rowVat = costRowsVat({
      rows: unitTables.flatMap(table => table.vatRows),
      vatPct: vatRate,
      billing,
    });
    const firstRowOfTable = unitTables.map((_, tableIndex) =>
      unitTables
        .slice(0, tableIndex)
        .reduce((n, table) => n + table.vatRows.length, 0),
    );
    return (
      <Stack vertical spacing="loose">
        {unitTables.map(({ unit, rows, showOwnerBase }, tableIndex) => {
          const vatOfRow = (rowIndex: number) =>
            rowVat[firstRowOfTable[tableIndex] + rowIndex];
          const tableRows: ISplitTableRow[] = [
            ...rows.map((row, rowIndex) => ({
              id: row.key,
              who: row.name,
              share: unitWeightText(unit.weight, unit.totalWeight),
              occupied: {
                days: row.days,
                total: unit.proration.periodDays,
                period: costPeriod,
                isVacant: row.kind !== "tenant",
              },
              amount: row.amount,
              vat: vatOfRow(rowIndex),
            })),
            ...(showOwnerBase
              ? [
                  {
                    id: `ownerbase-${unit.unitId}`,
                    who: ownerShareName,
                    share: "—",
                    amount: unit.proration.ownerBase,
                    vat: vatOfRow(rows.length),
                  },
                ]
              : []),
          ];
          return (
            <CollapsibleCard key={unit.unitId} title={unitLabelOf(unit.unitId)}>
              <SplitTable
                withOccupied
                rows={tableRows}
                totalLabel={getLocalizedText("costs.detail.property_subtotal")}
                total={{
                  amount: unit.proration.total,
                  vat: fromCents(
                    tableRows.reduce((sum, row) => sum + toCents(row.vat), 0),
                  ),
                }}
              />
            </CollapsibleCard>
          );
        })}
        <CardInset>
          <CostTotals amount={invoice.amountExclVat} vat={btw} />
        </CardInset>
      </Stack>
    );
  };

  // A unit's own cost: its tenants pro rata the days they lived there, its
  // vacancy and its owner's share; fully the owner's, one row.
  const renderUnitSplit = () => {
    if (isOwnerCost) {
      return (
        <SplitTable
          rows={[
            {
              id: "owner",
              who: getLocalizedText("costs.detail.owner_cost_share_pct", {
                pct: "100",
              }),
              share: "—",
              amount: invoice.amountExclVat,
              vat: btw,
            },
          ]}
          totalLabel={costTotalLabel}
          total={costTotal}
        />
      );
    }
    if (!areSingleRentalsLoaded) return renderLoading();
    const res = prorateByOccupancy(
      invoice.amountExclVat,
      tenantsPct,
      singleRentals.map(rental => rental.ref),
      { from: invoice.periodFrom, to: invoice.periodTo },
    );
    const distRows = [
      ...res.shares.map(share => ({
        kind: "tenant" as const,
        key: share.contractId,
        name: share.tenantName,
        fromIso: share.fromIso,
        days: share.days,
        amount: share.amount,
      })),
      ...res.vacancyIntervals.map((vacancy, index) => ({
        kind: "vacancy" as const,
        key: `vac-${index}`,
        name: getLocalizedText("costs.detail.owner_vacancy_full"),
        fromIso: vacancy.fromIso,
        days: vacancy.days,
        amount: vacancy.amount,
      })),
    ].sort((a, b) => a.fromIso.localeCompare(b.fromIso));
    const showOwnerBase = isShared && Math.abs(res.ownerBase) >= HALF_CENT;
    // Each row's part of the cost's VAT: what the settlement or the tenant's
    // payment request bills it (see costVat.utils).
    const ownerPartyId = invoice.unitId ?? "";
    const rowVat = costRowsVat({
      rows: [
        ...distRows.map(row => ({
          kind:
            row.kind === "tenant" ? ("tenant" as const) : ("owner" as const),
          partyId: row.kind === "tenant" ? row.key : ownerPartyId,
          amount: row.amount,
          outgoingVatPct:
            row.kind === "tenant" ? outgoingVat[row.key] : undefined,
        })),
        {
          kind: "owner" as const,
          partyId: ownerPartyId,
          amount: showOwnerBase ? res.ownerBase : 0,
        },
      ],
      vatPct: vatRate,
      billing,
    });
    return (
      <SplitTable
        withOccupied
        rows={[
          ...distRows.map((row, rowIndex) => ({
            id: row.key,
            who: row.name,
            // The tenants' part of the cost, its vacant days the owner's.
            share: unitCostShareText(tenantsPct) ?? "—",
            occupied: {
              days: row.days,
              total: res.periodDays,
              period: costPeriod,
              isVacant: row.kind !== "tenant",
            },
            amount: row.amount,
            vat: rowVat[rowIndex],
          })),
          ...(showOwnerBase
            ? [
                {
                  id: "ownerbase",
                  who: ownerShareName,
                  share: "—",
                  amount: res.ownerBase,
                  vat: rowVat[distRows.length],
                },
              ]
            : []),
        ]}
        totalLabel={costTotalLabel}
        total={costTotal}
      />
    );
  };

  // Whether the cost goes into a settlement. A cost billed right away or
  // marked paid goes into none (settlementEligible).
  const goesToSettlement =
    shownSettledInfo.settled || settlementEligible(invoice);

  // A building's cost not split yet: its total, the units' parts follow in
  // the settlement.
  const renderPendingSplit = () => (
    <Stack vertical spacing="tight">
      {goesToSettlement && (
        <Banner variation="info" hasDismiss={false}>
          {getLocalizedText("costs.detail.split.pending_settlement")}
        </Banner>
      )}
      <CostTotals amount={invoice.amountExclVat} vat={btw} />
    </Stack>
  );

  // How a settlement split the cost, named; what it still needs instead of
  // figures that would be a guess (an older snapshot).
  const renderSettlementSplit = (params: {
    split: ICostSplit;
    name?: string;
    totalLabel?: string;
  }) => {
    const {
      split,
      name,
      // An owner who sees only their own units: the rows add up to their
      // part, not to the cost (Y16).
      totalLabel = splitContext?.viewerPart
        ? getLocalizedText("costs.detail.your_share")
        : costTotalLabel,
    } = params;
    if (split.missing) {
      return (
        <Stack vertical spacing="tight">
          <TextStyle variation="subdued">{split.missing}</TextStyle>
          <CostTotals amount={invoice.amountExclVat} vat={btw} />
        </Stack>
      );
    }
    const rows = settlementSplitRows(split, openedFor);
    // The settlement's own amounts, added up in cents.
    const total = {
      amount: fromCents(
        rows.reduce((sum, row) => sum + toCents(row.amount), 0),
      ),
      vat: fromCents(rows.reduce((sum, row) => sum + toCents(row.vat), 0)),
    };
    return (
      <Stack vertical spacing="tight">
        {name && (
          <TextStyle variation="subdued" size="small">
            {getLocalizedText("costs.detail.split.title_in", { name })}
          </TextStyle>
        )}
        <SplitTable
          rows={rows}
          totalLabel={totalLabel}
          total={total}
          withOccupied={rows.some(row => row.occupied != null)}
        />
      </Stack>
    );
  };

  // A departure holds the leaving tenant's share only: the cost keeps its
  // own split, that share is a section of its own.
  const isDepartureSplit = !!splitContext?.isDeparture;

  const renderSplitBody = () => {
    if (settlementSplit && !isDepartureSplit) {
      return renderSettlementSplit({
        split: settlementSplit,
        name: splitSettlementName,
      });
    }
    if (!isBuilding) return renderUnitSplit();
    if (isSplitAtEntry) return renderBuildingSplit();
    if (settledSplit) {
      return renderSettlementSplit({ split: settledSplit, name: settledName });
    }
    return renderPendingSplit();
  };

  // A card of the drawer: its title, the cost's period under it, the split.
  // No period under the title: the Kost card names it (Lewis, 2026-10-19).
  const renderSplitSection = (title: string, body: React.ReactNode) => (
    <CollapsibleCard title={title}>{body}</CollapsibleCard>
  );

  // A building's cost split at entry names its Verdelingsmethode first.
  const renderSplitCard = () =>
    renderSplitSection(
      getLocalizedText("costs.modal.step.distribution"),
      isSplitAtEntry ? (
        <Stack vertical spacing="loose">
          {renderMethodRows()}
          {renderSplitBody()}
        </Stack>
      ) : (
        renderSplitBody()
      ),
    );

  // ── Detail betaalstatus huurder ───────────────────────────────────────────
  // Staging's payment status (Lewis, 2026-10-19): "Detail betaalstatus
  // huurder (0/1 betaald)", then one row per payment request the cost holds.
  // Only those: never a row projected from the tenants of the cost's period
  // (staging's "Eigenaarsaandeel (leegstand)" for the whole of a building's
  // cost still bound for a settlement). No card for a cost going through a
  // settlement, whose status the header names ("Nog af te rekenen",
  // "Opgenomen in …"), nor for the owner's own cost.

  // The payment requests the cost asked; a cost marked paid has them all
  // paid.
  const requestedSplits = isPaymentRequested(invoice) ? splits : [];
  const paidCount = invoice.paidAt
    ? requestedSplits.length
    : requestedSplits.filter(split => !!split.paidAt).length;

  // One payment request: the tenant, their unit's full address under the
  // name, the status on the right (Lewis, 2026-10-19: no amount here).
  const paymentRowAddress = (split: PaymentSplit): string | undefined => {
    const propertyId =
      split.propertyId != null ? String(split.propertyId) : invoice.unitId;
    if (!propertyId) return undefined;
    const property = assignable.find(p => String(p.id) === propertyId);
    return property ? unitAddressLabel(property) : unitLabelOf(propertyId);
  };

  const renderPaymentRow = (split: PaymentSplit) => {
    const orderId = split.paymentOrderId;
    const address = paymentRowAddress(split);
    return (
      <PaymentRow key={split.id}>
        <div className="who">
          <div className="name">
            {split.payerName || getLocalizedText("costs.field.tenant")}
          </div>
          {address && (
            <div className="sub">
              <span>{address}</span>
            </div>
          )}
        </div>
        <SplitPaymentLozenge split={split} paidAt={invoice.paidAt} />
        {orderId && (
          <div className="actions">
            <DropdownMenu
              actions={[
                {
                  content: getLocalizedText("costs.action.go_to_payment"),
                  onClick: () => goToPayment(orderId),
                },
              ]}
            >
              <ListMenuButton
                type="button"
                aria-label={getLocalizedText("costs.action.more")}
              >
                <Icon source="navigationMenuVertical" />
              </ListMenuButton>
            </DropdownMenu>
          </div>
        )}
      </PaymentRow>
    );
  };

  // The tenants' payments, when the cost was billed right away or marked
  // paid: per payment request, or the cost as a whole; nothing for a cost
  // whose payment is still to be asked (the header says "Te vragen").
  const renderPaymentStatus = () => {
    if (isOwnerCost) return null;
    if (requestedSplits.length > 0) {
      return <div>{requestedSplits.map(renderPaymentRow)}</div>;
    }
    if (invoice.paidAt) {
      return (
        <StatusLozenge appearance="success">
          {getLocalizedText("costs.detail.paid_on", {
            date: dayText(invoice.paidAt),
          })}
        </StatusLozenge>
      );
    }
    // A cost asked before splits were stored.
    if (invoice.target === "direct" && invoice.paymentRequestedAt) {
      return (
        <Inline>
          <StatusLozenge appearance="info">
            {getLocalizedText("costs.status.invitation_sent")}
          </StatusLozenge>
          <TextStyle variation="subdued" size="small">
            {getLocalizedText("costs.detail.sent_on", {
              date: dayText(invoice.paymentRequestedAt),
            })}
          </TextStyle>
        </Inline>
      );
    }
    return null;
  };

  // The card's title, with how many of the cost's payment requests are
  // paid.
  const paymentCardTitle = () => {
    const title = getLocalizedText("costs.detail.tenant_payment_status");
    if (requestedSplits.length === 0) return title;
    return `${title} (${getLocalizedText("costs.status.paid_of_total", {
      paid: String(paidCount),
      total: String(requestedSplits.length),
    })})`;
  };

  const renderPaymentCard = () => {
    const paymentStatus = renderPaymentStatus();
    if (!paymentStatus) return null;
    return (
      <CollapsibleCard title={paymentCardTitle()}>
        {paymentStatus}
      </CollapsibleCard>
    );
  };

  // ── The departure's share ─────────────────────────────────────────────────

  const renderDepartureSplitCard = () => {
    if (!settlementSplit || !isDepartureSplit) return null;
    return renderSplitSection(
      splitSettlementName
        ? getLocalizedText("costs.detail.split.title_in", {
            name: splitSettlementName,
          })
        : getLocalizedText("costs.field.distribution"),
      renderSettlementSplit({
        split: settlementSplit,
        totalLabel: getLocalizedText("costs.field.total"),
      }),
    );
  };

  const cards = (
    <Cards>
      {renderCostCard()}
      {renderSplitCard()}
      {renderPaymentCard()}
      {renderDepartureSplitCard()}
    </Cards>
  );

  const dropdownActions: {
    content: React.ReactNode;
    onClick: () => void;
  }[] = [];
  if (onEdit && !isSettled) {
    dropdownActions.push({
      content: getLocalizedText("costs.action.edit_item"),
      onClick: onEdit,
    });
  }
  if (onDelete) {
    dropdownActions.push({
      content: (
        <TextStyle variation="negative">
          {getLocalizedText("costs.detail.delete_cost")}
        </TextStyle>
      ),
      onClick: onDelete,
    });
  }
  const dropdown = dropdownActions.length > 0 ? dropdownActions : undefined;

  return (
    <Page
      title={titleText}
      metadata={
        <>
          <Spacer weight={ESpacerWeight.W08} />
          {headerLozenges}
        </>
      }
      dropdown={dropdown as any}
      setDocumentTitle={false}
    >
      {cards}
    </Page>
  );
};
